<template>
  <div class="attr-panel" :class="{'is-collapsed': isCollapsed, 'is-expanded-view': isExpandedView}" @click="panelClickHandler">
    <collapse-btn class="attr-panel__expand-btn" v-if="isCollapsed" :hide-text="true"></collapse-btn>

    <header class="attr-panel__header">
      <div class="attr-panel__header-nav">
        <panel-size-btn @click.native="togglePanelSize" :is-expanded-view="isExpandedView"></panel-size-btn>
        <collapse-btn @click.native="toggleCollapse"></collapse-btn>
      </div>

      <div class="attr-panel__target-info" v-if="currentTarget">
        <v-row justify="start">
          <v-col v-if="!editModeActive">
            {{ currentTarget.display_name }}
          </v-col>
          <v-col v-else md="6">
            <target-name
            :isNewTarget="false"
            :initialValue="originalDisplayName"
            :displayOverride="currentTarget.display_name"
            :targetType="currentTarget.target"
            :attributePanel="true"
            @updateName="updateName"
            @updateNameIsValid="validateName"/>
          </v-col>
          <v-col v-if="!isAdvertiserEditable && currentTarget && currentTarget.advertiser && currentTarget.advertiser.Name">
            {{ currentTarget.advertiser.Name }}
          </v-col>
          <v-col v-if="isAdvertiserEditable && currentTarget && currentTarget.advertiser" md="6">
            <!-- <advertiser-select-new
              ref="advertiserSelect"
              placeholder="Select"
              :value="currentTarget.advertiser"
              @input="changeAdvertiser"
              ></advertiser-select-new> -->
            <advertiser-select
              ref="advertiserSelect"
              placeholder="Select"
              :attributePanel="true"
              @update="val => changeAdvertiser(val)"
              v-model="currentTarget.advertiser"
              :initialValue="currentTarget.advertiser"
              :showAdvertiserId="false"
            ></advertiser-select>
          </v-col>
        </v-row>
        <div class="attr-panel__target-info__edit-icon" v-if="targetEditable">
          <v-icon @click.native="toggleEditMode" dark>mdi-pencil</v-icon>
        </div>
      </div>

      <search-input class="attr-panel__search" ref="searchInput" v-model="searchTerm" :debounce="500"></search-input>
      <bread-crumbs v-show="!isSearchActive" :crumbs="crumbs" @click="crumbClickHandler"></bread-crumbs>
      <back-btn class="attr-panel__back-btn" v-show="isSearchActive" @click.native="clearSearch">Back To Browse</back-btn>
    </header>

    <div class="attr-panel__body">
      <small-text v-if="tooManySearchResults">
        Too many results to display ({{ searchResultsCount }}). Please refine your search.
      </small-text>
      <search-results ref="searchResults" v-else-if="isSearchActive"
                      :results="searchResults"
                      :count="searchResultsCount"
                      @item-selected="itemSelectedHandler"></search-results>
        <list v-if="currentList" v-show="!isSearchActive"
              :list-group="currentList"
              id="attributePanelID"
              @item-selected="itemSelectedHandler"></list>
    </div>
  </div>
</template>

<script>
  import { SET_DATA_SOURCES } from '@/views/workbench/LAS/store/mutations'
  import SearchInput from '@/components/common/searchInput'
  import SearchResults from './SearchResults.vue'
  import CollapseBtn from './CollapseButton.vue'
  import PanelSizeBtn from './PanelSizeBtn'
  import BackBtn from './BackButton.vue'
  import BreadCrumbs from './BreadCrumbs.vue'
  import List from './List.vue'
  import SmallText from './SmallText.vue'
  import SearchService from './services/search'
  import AttributeTree from './models/attributeTree'
  import EditIcon from '@/appComponents/workbench/attributePanel/assets/EditIcon'
  import AdvertiserSelectNew from '@/appComponents/workbench/newSelect/AdvertiserSelectNew.vue'
  import AdvertiserSelect from '@/appComponents/targets/components/AdvertiserSelect'
  import TargetName from '@/appComponents/targets/createTarget/components/TargetName'
  import { ACTIVATION_STATUS, INTENTS } from '@/appLogic/targets/Constants'
  import { SET_TARGET_NAME, SET_TARGET_ADVERTISER } from '@/appLogic/targets/currentTarget/store/actions.js'
  import { SET_TARGET_DEFINITION_DIRTY } from '@/views/workbenchCanvas/store/WorkbenchWCanvasSlotActionsGenerator'

  export default {
    components: {
      CollapseBtn,
      PanelSizeBtn,
      BackBtn,
      SearchInput,
      SearchResults,
      List,
      SmallText,
      BreadCrumbs,
      EditIcon,
      AdvertiserSelectNew,
      AdvertiserSelect,
      TargetName
    },
    props: {
      isCollapsed: {
        type: Boolean,
        default: false
      },
      isExpandedView: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        currentList: null,
        searchTerm: '',
        searchResults: [],
        editModeActive: false,
        originalDisplayName: null,
        originalAdvertiserId: null,
        nameIsValid: null,
        newNameOfTarget: null,
        previousDirtyState: false
      }
    },
    computed: {
      crumbs () {
        return this.currentList && this.currentList.getPath().map(node => {
          return {
            name: node.model.name,
            node
          }
        })
      },
      tooManySearchResults () {
        return this.searchResultsCount > 500
      },
      searchResultsCount () {
        return this.searchResults.reduce((currValue, resultSet) => resultSet.results.length + currValue, 0)
      },
      isSearchActive () {
        return this.searchTerm.length > 0
      },
      currentTarget () {
        return this.$store.getters.currentTarget
      },
      isDraft () {
        return this.currentTarget && this.currentTarget.activation_status && this.currentTarget.activation_status === ACTIVATION_STATUS.DRAFT
      },
      isWorking () {
        return this.currentTarget && this.currentTarget.activation_status && this.currentTarget.activation_status === ACTIVATION_STATUS.WORKING
      },
      targetEditable () {
        return this.currentTarget && this.currentTarget.activation_status && (this.isDraft || this.isWorking)
      },
      isAdvertiserEditable () {
        const marketingIntent = this.currentTarget && this.currentTarget.metadata && (this.currentTarget.metadata.intents || []).find(i => i.Name === INTENTS.Marketing.Name)
        return this.currentTarget && this.currentTarget.advertiser && this.currentTarget.advertiser.Name && this.editModeActive && !marketingIntent
      }
    },
    watch: {
      searchTerm (newValue) {
        if (newValue.length < 1) {
          this.searchResults = []
        } else {
          this.searchResults = this.searchService.search(newValue)
        }
      },
      currentTarget () {
        this.editModeActive = false
      },
    },
    methods: {
      itemSelectedHandler (item) {
        this.currentList = item
      },
      crumbClickHandler (crumb) {
        this.currentList = crumb.node
      },
      clearSearch () {
        this.$refs.searchInput.clearSearch()
      },
      panelClickHandler (e) {
        if (this.isCollapsed) this.toggleCollapse(e)
      },
      toggleCollapse (e) {
        e.stopPropagation()
        this.$emit('collapse-clicked')
      },
      togglePanelSize () {
        this.$emit('change-size-click')
      },
      toggleEditMode () {
        if (this.editModeActive) {
          let changedName = this.originalDisplayName !== this.currentTarget.display_name
          let changedAdvertiser = this.currentTarget.advertiser && this.originalAdvertiserId !== this.currentTarget.advertiser.Advertiser_id
          if (changedName || changedAdvertiser) {
            this.$store.dispatch(SET_TARGET_DEFINITION_DIRTY, true)
            this.previousDirtyState = true
          } else if (this.previousDirtyState) {
            this.$store.dispatch(SET_TARGET_DEFINITION_DIRTY, true)
          }
        } else {
          if (this.previousDirtyState) {
            this.$store.dispatch(SET_TARGET_DEFINITION_DIRTY, false)
          }
        }
        this.originalDisplayName = this.currentTarget.display_name
        this.originalAdvertiserId = this.currentTarget.advertiser && this.currentTarget.advertiser.Advertiser_id
        this.editModeActive = !this.editModeActive
      },
      updateName (name) {
        this.newNameOfTarget = name
      },
      validateName (isValid) {
        this.nameIsValid = isValid
        const validAndNameIsDiff = this.nameIsValid && this.newNameOfTarget && this.newNameOfTarget !== this.originalDisplayName
        if (validAndNameIsDiff) {
          this.$store.dispatch(SET_TARGET_NAME, this.newNameOfTarget)
        } else {
          this.currentTarget.display_name = this.originalDisplayName
          this.$store.dispatch(SET_TARGET_DEFINITION_DIRTY, false)
        }
      },
      changeAdvertiser (ad) {
        if (ad && ad.Advertiser_id) {
          this.$store.dispatch(SET_TARGET_ADVERTISER, ad)
        }
      }
    },
    created () {
      this.searchService = new SearchService()
      this.attributesTree = new AttributeTree()
      this.unsubscribe = this.$store.subscribe(({ type, payload }, state) => {
        if (type !== SET_DATA_SOURCES) return
        payload.forEach(dataSource => {
          this.searchService.addDataSource(dataSource.flat)
          this.attributesTree.addDataSource(dataSource.data)
        })
        this.currentList = Object.freeze(this.attributesTree.root)
      })
    },
    beforeDestroy () {
      this.unsubscribe()
    }
  }
</script>

<style lang="scss">
  $container-h-padding: rem(20);
  $bg-color: #435262;
  $header-height: rem(130);
  $header-padding-top: rem(8);
  $header-padding-bottom: rem(14);
  $header-full-height: $header-height + $header-padding-top + $header-padding-bottom;

  .attr-panel {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    background-color: $bg-color;
    height: 100vh;
    color: white;
    overflow: hidden;

    &:after {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      left: rem(-10);
      right: rem(-10);
      height: rem(50);
      box-shadow: 0 rem(-18) rem(30) rgba(#000000, .3) inset;
      pointer-events: none;
    }

    &.is-collapsed {
      cursor: pointer;
      transition: background-color .15s ease-out,
                  box-shadow .15s ease-out;

      &:hover {
        background-color: lighten($bg-color, 5%);
        box-shadow: rem(-2) 0 0 color(yellow);
      }

      .attr-panel__body,
      .attr-panel__header {
        opacity: 0;
        transition-duration: 0s;
        transition-delay: 0s;
      }
    }

    &__body,
    &__header {
      transition: opacity .15s $global-easing .18s;
    }

    &__header {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      flex: 0 0 auto;
      // height: rem(190);
      padding: $header-padding-top $container-h-padding $header-padding-bottom $container-h-padding;
      border-bottom: rem(1) solid color(grays, light);
      box-sizing: border-box;
      margin-top: 1em;
    }

    &__header-nav {
      display: flex;
    }

    &__target-info {
      display: flex;
      font-size: 1.2em;
      margin-top: rem(5);
      flex-flow: row nowrap;
      justify-content: space-between;

      &__editable-fields {
        display: flex;
        flex-flow: column;

        &__name-input {
          background-color: #556678;
          border-top: 1px solid #2f3943;
          border-left: 1px solid #2f3943;
          border-right: 1px solid #2f3943;
          border-bottom-width: 1px;
          color: white;
          text-align: left;
        }
        &__advertiser {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }

        &__advertiser-select {
          background-color: #556678;
          border: 1px solid #2f3943;
          margin-top: 5px;
        }
      }

      &__edit-icon {
        cursor: pointer;
      }
    }

    &__search {
      margin-top: rem(10);
      margin-bottom: rem(25);
    }

    &__body {
      position: relative;
      display: flex;
      flex-direction: column;
      height: calc(100% - #{$header-full-height});
      padding-top: $container-h-padding;
      padding-left: $container-h-padding;
    }

    &__back-btn {
      align-self: flex-start;
    }

    &__expand-btn {
      width: 100%;
      padding: 0;
      margin-top: rem(17);
      transform: rotateZ(180deg);
    }
  }
</style>



// WEBPACK FOOTER //
// AttributePanel.vue?2acde170